import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import _ from 'underscore';

import { Panel, PrimaryButton, DefaultButton, MessageBar, MessageBarType, DefaultPalette } from '@bingads-webui-react/fabric-control';
import { LineByLineFormGroup } from '@bingads-webui-cc-react/layout';
import { RadioGroup, Input } from '@bingads-webui-react/primitive-validation';
import { getBudgetRange } from '@bingads-webui-campaign-legacy/common-grid-config/budget/get-budget-range';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { mergeI18n } from '@bingads-webui-campaign-react/i18n-merger';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';
import { Constants } from '@bingads-webui-clientcenter/entity-utils';

import { UiBlocker } from './ui-blocker';
import { CustomizeBudgetKey } from '../constants';
import { getBudgetUpdatePanelStyles } from '../styles';
import { formatCurrency, formatInteger } from '../utils';

const { BudgetRecommendationItemType } = Constants;

export const BudgetUpdatePanel = ({
  i18n: i18nFromProps,
  currencyCode,
  dailyBudget,
  lifetimeBudget,
  budgetId,
  budgetRecommendations,
  onSave,
  onCancel,
  isSaving,
}) => {
  const i18n = useMemo(() => mergeI18n(buildLegacyI18n(i18nFromProps, [ValidationStringMap]), i18nFromProps), [i18nFromProps]);
  const classes = useDefaultStyles(getBudgetUpdatePanelStyles);
  const { Daily: { Min: dailyMin, Max: dailyMax }, Lifetime: { Min: lifetimeMin, Max: lifetimeMax } } = useMemo(() => getBudgetRange(currencyCode), [currencyCode]);
  const schema = useMemo(() => ({
    properties: {
      CustomizeBudget: {
        type: 'number',
        required: true,
        minimum: dailyBudget !== null ? dailyMin : lifetimeMin,
        maximum: dailyBudget !== null ? dailyMax : lifetimeMax,
      },
    },
  }), [dailyMin, dailyMax, lifetimeMin, lifetimeMax, dailyBudget]);
  const radioItems = useMemo(() => [...budgetRecommendations, { Comment: CustomizeBudgetKey }], [budgetRecommendations]);
  const customizeItem = useMemo(() => _.findWhere(radioItems, { Comment: CustomizeBudgetKey }), [radioItems]);
  const [isValid, setIsValid] = useState(false);
  const [custimizeBudget, setCustomizeBudget] = useState(dailyBudget || lifetimeBudget);
  const [selectedItem, setSelectedItem] = useState(() => _.findWhere(radioItems, { Comment: BudgetRecommendationItemType.Suggested }) || radioItems[0]);
  const isSharedBudget = budgetId > 0;

  const customValidator = (value, defaultValidator) => {
    if (selectedItem !== customizeItem) {
      return [];
    }

    return defaultValidator(value);
  };

  const onClickSave = () => {
    onSave({ budget: selectedItem.Comment === CustomizeBudgetKey ? custimizeBudget : selectedItem.EstimatedBudget });
  };

  const onRenderFooterContent = () => (
    <div>
      {isSharedBudget && (
        <div className={classnames('panel-footer-info', classes.PanelFooterInfo)}>
          <MessageBar className="shared-budget-alert" messageBarType={MessageBarType.info} styles={{ root: { backgroundColor: DefaultPalette.themeLighterAlt } }}>
            {i18n.getString(_TL_('Notice: What you are editing is a shared budget, which is consumed across multiple campaigns.'))}
          </MessageBar>
        </div>
      )}
      <div className="panel-buttons">
        <PrimaryButton onClick={onClickSave} disabled={!isValid || isSaving} className="btn-submit-budget-update">
          {i18n.getString(_TL_('Apply'))}
        </PrimaryButton>
        <DefaultButton disabled={isSaving} onClick={onCancel} className="btn-cancel-budget-update">
          {i18n.getString(_TL_('Cancel'))}
        </DefaultButton>
      </div>
    </div>
  );

  const renderItem = ({
    Comment, EstimatedBudget, EstimatedClicksIncrease, EstimatedRevenueIncrease,
  }) => {
    if (Comment === CustomizeBudgetKey) {
      return (
        <>
          <span className="budget-update-panel-budget-option-customize">{i18n.getString(_TL_('Customize'))}</span>
          <div className={classnames('budget-update-panel-customize-input', classes.CustomizeInput)}>
            <Input
              key={selectedItem.Comment === CustomizeBudgetKey}
              validationProps={{
                i18n,
                validator: customValidator,
                onValidate: (value) => {
                  setCustomizeBudget(value.data[CustomizeBudgetKey]);
                  setIsValid(value.isValid);
                },
                field: CustomizeBudgetKey,
                data: { [CustomizeBudgetKey]: custimizeBudget },
                schema,
                alwaysShowError: custimizeBudget !== null,
              }}
              placeholder={selectedItem === customizeItem ? i18n.getString(_TL_('Specify the amount')) : undefined}
              disabled={selectedItem !== customizeItem}
            />
          </div>
        </>
      );
    }

    const budget = formatCurrency({ i18n, value: EstimatedBudget, currencyCode });
    const budgetRow = Comment === BudgetRecommendationItemType.Suggested
      ? (
        <span className="budget-update-panel-budget-option-recommended">
          {dailyBudget !== null
          ? i18n.getString(_TL_('{{budget}} /day (recommended)'), { budget })
          : i18n.getString(_TL_('{{budget}} (recommended)'), { budget })}
        </span>)
      : (
        <span className="budget-update-panel-budget-option">
          {dailyBudget !== null
          ? i18n.getString(_TL_('{{budget}} /day'), { budget })
          : i18n.getString(_TL_('{{budget}}'), { budget })}
        </span>);
    let impactRow = null;
    if (_.isNumber(EstimatedClicksIncrease) && EstimatedClicksIncrease !== 0) {
      const clicks = formatInteger({ i18n, value: EstimatedClicksIncrease });
      impactRow = (
        <div className={classnames('budget-update-panel-budget-option-impact', 'estimated-clicks-increase', classes.BudgetOptionImpact)}>
          {i18n.getString(_TL_('Weekly estimates: {{clicks}} Clicks'), { clicks })}
        </div>
      );
    } else if (_.isNumber(EstimatedRevenueIncrease) && EstimatedRevenueIncrease !== 0) {
      const revenue = formatCurrency({ i18n, value: EstimatedRevenueIncrease, currencyCode });
      impactRow = (
        <div className={classnames('budget-update-panel-budget-option-impact', 'estimated-revenue-increase', classes.BudgetOptionImpact)}>
          {i18n.getString(_TL_('Expected revenue lift: {{revenue}}'), { revenue })}
        </div>
      );
    }

    return (
      <>
        {budgetRow}
        {impactRow}
      </>
    );
  };

  const renderHeaderText = () => {
    if (isSharedBudget && lifetimeBudget !== null) {
      return i18n.getString(_TL_('Lifetime budget (shared)'));
    } else if (lifetimeBudget !== null) {
      return i18n.getString(_TL_('Lifetime budget'));
    } else if (isSharedBudget && dailyBudget !== null) {
      return i18n.getString(_TL_('Daily budget (shared)'));
    }
    return i18n.getString(_TL_('Daily budget'));
  };

  return (
    <Panel
      className={classnames('budget-update-panel', classes.Panel)}
      layerProps={{ className: classes.Layer }}
      closeButtonAriaLabel={i18n.getString(_TL_('Cancel'))}
      headerText={renderHeaderText()}
      isOpen
      isLightDismiss
      onDismiss={isSaving ? null : onCancel}
      isFooterAtBottom
      onRenderFooterContent={onRenderFooterContent}
    >
      <UiBlocker blocking={isSaving} loadingText={i18n.getString(_TL_('Saving...'))}>
        <LineByLineFormGroup isStatic contentColClass="col-xs-12" className="budget-update-panel-radio-group" >
          <RadioGroup
            name="budget-update-panel-radio-group"
            items={radioItems}
            checked={selectedItem}
            renderItem={renderItem}
            onChange={setSelectedItem}
          />
        </LineByLineFormGroup>
      </UiBlocker>
    </Panel>
  );
};

BudgetUpdatePanel.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
    formatCurrency: PropTypes.func.isRequired,
  }).isRequired,
  currencyCode: PropTypes.string.isRequired,
  dailyBudget: PropTypes.number,
  lifetimeBudget: PropTypes.number,
  budgetId: PropTypes.number,
  budgetRecommendations: PropTypes.arrayOf(PropTypes.shape({
    Comment: PropTypes.oneOf(_.values(BudgetRecommendationItemType)),
    EstimatedBudget: PropTypes.number,
    EstimatedClicksIncrease: PropTypes.number,
    EstimatedRevenueIncrease: PropTypes.number,
  })),
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
};

BudgetUpdatePanel.defaultProps = {
  dailyBudget: null,
  lifetimeBudget: null,
  budgetId: null,
  budgetRecommendations: [],
  isSaving: false,
};
